/**
 * Process billing API customer_statement rows for the billing-history table.
 *
 * Statement PDFs / billingAnalysis use the same rules:
 * - Seed opening from the earliest "Balance b/f" (debit owed / credit in favour)
 * - Ignore "Total", "Balance c/f", and later period "Balance b/f" markers
 * - Running balance = opening + sum(debit - credit) over real transactions
 */

const asDate = (value) => new Date(value);

export const isStatementPeriodMarker = (transaction) => {
  const name = String(transaction || "")
    .trim()
    .toLowerCase();
  return name === "total" || name === "balance c/f" || name === "balance b/f" || name === "opening balance";
};

export const isOpeningBalanceRow = (transaction) => {
  const name = String(transaction || "")
    .trim()
    .toLowerCase();
  return name === "balance b/f" || name === "opening balance";
};

export const statementRowSignedAmount = (row) => {
  const debit = Number(row?.debit || 0);
  const credit = Number(row?.credit || 0);
  return debit - credit;
};

export const openingBalanceFromRow = (row) => {
  const debit = Number(row?.debit || 0);
  const credit = Number(row?.credit || 0);
  if (debit > 0) return debit;
  if (credit > 0) return -credit;
  return 0;
};

/**
 * @param {Array<object>} rows - raw customer_statement reply
 * @returns {Array<object>} chronological rows with signed amount + runningBalance numbers
 */
export function processCustomerStatementHistory(rows) {
  const list = Array.isArray(rows) ? rows.slice() : [];
  if (!list.length) return [];

  const sorted = list.sort((a, b) => asDate(a.datetime) - asDate(b.datetime));

  let runningBalance = 0;
  let openingApplied = false;
  const processed = [];

  for (let i = 0; i < sorted.length; i += 1) {
    const entry = sorted[i];
    const transaction = String(entry?.transaction || "");

    if (isOpeningBalanceRow(transaction)) {
      if (!openingApplied) {
        runningBalance = openingBalanceFromRow(entry);
        openingApplied = true;
      }
      continue;
    }

    if (isStatementPeriodMarker(transaction)) {
      continue;
    }

    const amount = statementRowSignedAmount(entry);
    runningBalance += amount;

    processed.push({
      datetime: entry.datetime,
      transaction,
      ref: entry.ref ?? "-",
      debit: Number(entry.debit || 0),
      credit: Number(entry.credit || 0),
      amount,
      runningBalance,
      raw: entry,
    });
  }

  return processed;
}
